import { AlertTriangle, CheckCircle2, Info, Trash2 } from 'lucide-react'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Button } from './Button'
import { Dialog } from './Dialog'

export type ConfirmTone = 'default' | 'danger' | 'success' | 'warning' | 'info'

export type ConfirmOptions = {
  title: string
  message?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: ConfirmTone
}

type Resolver = (value: boolean) => void

const ConfirmContext = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null)

/**
 * Mount once near the root (inside RouterProvider) to enable `useConfirm()`
 * throughout the app. Renders a single Dialog reused for every confirm call.
 */
export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [opts, setOpts] = useState<ConfirmOptions | null>(null)
  const resolverRef = useRef<Resolver | null>(null)

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    setOpts(options)
    setOpen(true)
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve
    })
  }, [])

  const finish = useCallback((value: boolean) => {
    setOpen(false)
    const r = resolverRef.current
    resolverRef.current = null
    // small delay so the closing animation plays before the resolver runs
    if (r) setTimeout(() => r(value), 0)
  }, [])

  const value = useMemo(() => confirm, [confirm])

  const tone = opts?.tone ?? 'default'
  const icon = iconFor(tone)
  const confirmButtonVariant: 'primary' | 'destructive' =
    tone === 'danger' ? 'destructive' : 'primary'

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {opts && (
        <Dialog open={open} onClose={() => finish(false)} title={opts.title} size="sm">
          <div className="p-5">
            <div className="flex items-start gap-3">
              <div
                className={`shrink-0 size-9 rounded-full grid place-items-center ${ringFor(tone)}`}
              >
                {icon}
              </div>
              <div className="flex-1 min-w-0">
                {opts.message && (
                  <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">
                    {opts.message}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button type="button" variant="ghost" onClick={() => finish(false)}>
                {opts.cancelLabel ?? 'Annulla'}
              </Button>
              <Button
                type="button"
                variant={confirmButtonVariant}
                onClick={() => finish(true)}
                autoFocus
              >
                {opts.confirmLabel ?? 'Conferma'}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </ConfirmContext.Provider>
  )
}

/**
 * Returns an async function that displays the custom confirm dialog and
 * resolves to true (confirmed) or false (cancelled / closed).
 *
 * Usage:
 *   const confirm = useConfirm()
 *   const ok = await confirm({ title: 'Eliminare X?', message: 'Operazione irreversibile.', tone: 'danger' })
 *   if (!ok) return
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    throw new Error('useConfirm must be used inside <ConfirmDialogProvider>')
  }
  return ctx
}

function iconFor(tone: ConfirmTone): ReactNode {
  const cls = 'size-4.5'
  switch (tone) {
    case 'danger':
      return <Trash2 className={cls} />
    case 'success':
      return <CheckCircle2 className={cls} />
    case 'warning':
      return <AlertTriangle className={cls} />
    case 'info':
      return <Info className={cls} />
    default:
      return <Info className={cls} />
  }
}

function ringFor(tone: ConfirmTone): string {
  switch (tone) {
    case 'danger':
      return 'bg-pink-500/15 text-pink-300 shadow-[0_0_0_1px_rgba(244,114,182,0.3)_inset]'
    case 'success':
      return 'bg-emerald-500/15 text-emerald-300 shadow-[0_0_0_1px_rgba(52,211,153,0.3)_inset]'
    case 'warning':
      return 'bg-amber-500/15 text-amber-300 shadow-[0_0_0_1px_rgba(245,158,11,0.3)_inset]'
    case 'info':
      return 'bg-cyan-500/15 text-cyan-300 shadow-[0_0_0_1px_rgba(34,211,238,0.3)_inset]'
    default:
      return 'bg-zinc-500/15 text-zinc-300 shadow-[0_0_0_1px_rgba(161,161,170,0.3)_inset]'
  }
}
